import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { scroller } from 'react-scroll';

import BreadCrumbs from './BreadCrumbs';
import Button from '../../global/Button';
import TransparentButton from '../../global/Button/TransparentButton';
import Heading from '../Heading';
import InnerWrapper from '../../global/InnerWrapper';
import List from '../../global/List';
import NavLink from '../../global/NavLink';
import TextHighlight from '../../global/TextHighlight';
import { product } from '../../../styles/theme';

const FirstBreakPoint = '1300px';
const MobileBreakPoint = '700px';

const ButtonsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const DetailsWrapper = styled.div`
  height: auto;
  padding: 60px 0 0 40px;
  position: relative;

  @media (min-width: ${MobileBreakPoint}) {
    width: 55%;
  }

  @media (max-width: ${FirstBreakPoint}) {
    padding: 0px 0 0 20px;
  }

  @media (max-width: ${MobileBreakPoint}) {
    padding: 11px 0 0 0;
  }
`;

const Flag = styled.div`
  align-items: center;
  background-color: rgba(200, 200, 200, 0.6);
  display: flex;
  height: 100%;
  justify-content: center;
  max-height: 525px;
  position: absolute;
  top: 0;
  width: 100%;
`;

const FlagText = styled.div`
  color: ${p => p.theme.color.textPrimary};
  font-family: ${p => p.theme.font.sourceSans};
  font-size: 18px;
  font-weight: ${p => p.theme.fontWeight.bold};
  text-transform: uppercase;
`;

const Image = styled.img`
  max-height: 525px;
  min-height: 325px;
  width: 100%;
  height: auto;

  @media (max-width: ${MobileBreakPoint}) {
    height: 100%;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  justify-content: center;
  max-height: 525px;

  @media (min-width: ${MobileBreakPoint}) {
    width: 45%;
  }

  @media (max-width: ${FirstBreakPoint}) {
    min-width: auto;
  }

  @media (max-width: ${MobileBreakPoint}) {
    display: flex;
  }
`;

const ThreeDModelLink = styled.a`
  position: absolute;
  right: 0;
  top: 50%;
`;

const OverlayWrapper = styled.div`
  position: relative;
`;

const ProductDescription = styled.p`
  color: ${p => p.theme.color.textPrimary};
  font-family: ${p => p.theme.font.sourceSans};
  font-size: 16px;
  font-weight: ${p => p.theme.fontWeight.regular};
  line-height: 24px;
  margin: 0 50px 24px 0;
  text-align: left;

  @media (max-width: ${MobileBreakPoint}) {
    line-height: 15px;
    margin: 0 0 15px 0;
  }
`;

const ProductTitle = styled(Heading)`
  padding: 0 0 21px 0;

  @media (max-width: ${MobileBreakPoint}) {
    font-size: 20px;
    margin: 0 0 5px 0;
    padding: 0 0 7.5px 0;
  }
`;

const ButtonShared = css`
  white-space: nowrap;
  width: 100%;
  max-width: none;
  flex-grow: 1;
  flex-shrink: 0;
  flex-basis: calc(50% - 30px);
  margin: 10px 5px;

  @media ${p => p.theme.media.largePhone} {
    flex-basis: 100%;
  }
`;

const RebateWidgetWrapper = styled.div`
  ${ButtonShared};
`;

const StyledBreadCrumbs = styled(BreadCrumbs)`
  margin-bottom: 15px;

  @media (max-width: ${MobileBreakPoint}) {
    display: none;
  }
`;

const StyledInnerWrapper = styled(InnerWrapper)`
  padding: 46px 0 92px;

  @media (max-width: ${MobileBreakPoint}) {
    padding: 28px 0 49px;
  }
`;

const StyledList = styled(List)`
  margin-top: 5px;
  margin-bottom: 20px;

  @media (max-width: ${MobileBreakPoint}) {
    margin-bottom: 0px;
  }

  li {
    line-height: 20px;

    @media (max-width: ${MobileBreakPoint}) {
      letter-spacing: 0.04px;
    }
  }
`;

const StyledNavLink = styled(NavLink)`
  ${ButtonShared}
`;

const SubWrapper = styled.div`
  display: flex;
  flex-direction: row;

  @media (max-width: ${MobileBreakPoint}) {
    display: block;
    width: 100%;
  }
`;

const CustomTankButton = styled(TransparentButton)`
  width: 100%;
`;

const DocsButton = styled(Button)`
  ${ButtonShared};
  border: 1px solid ${p => p.theme.color.primary};
  padding: 12px;
  width: 100%;

  @media (min-width: ${MobileBreakPoint}) {
    width: calc(50% - 10px);
  }
`;

const warrantyClicked = () => {
  scroller.scrollTo('lit', {
    offset: -product.tabs.height - 141,
    smooth: true
  });
};

export default class ProductHeader extends React.Component {
  static propTypes = {
    archived: PropTypes.bool,
    breadCrumbs: PropTypes.arrayOf(PropTypes.object).isRequired,
    models: PropTypes.arrayOf(PropTypes.object).isRequired,
    productDescription: PropTypes.string.isRequired,
    productFeatures: PropTypes.arrayOf(PropTypes.string).isRequired,
    productImage: PropTypes.shape({
      alt: PropTypes.string,
      src: PropTypes.string
    }),
    productName: PropTypes.string.isRequired,
    productNameHighlight: PropTypes.string,
    showDocsButton: PropTypes.bool.isRequired,
    showCustomTankButton: PropTypes.bool.isRequired,
    threeDModelUrl: PropTypes.string
  };

  componentDidMount() {
    if (typeof window === 'undefined') {
      return;
    }

    const models = this.props.models.map(model => model.model);

    window._ecr = {};
    window._ecr.ecorebates = models;
    window._ecr.options = {
      rebateConsumerTypes: ['commercial', 'residential']
    };

    const scriptUrl = '//static.ecorebates.com/ui/widgets/lochinvar/product.js';

    if (!document.querySelector(`script[src="${scriptUrl}"]`)) {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = scriptUrl;
      script.async = true;
      document.body.appendChild(script);
    }
  }

  render() {
    const {
      archived,
      breadCrumbs,
      productDescription,
      productFeatures,
      productImage,
      productName,
      productNameHighlight,
      showCustomTankButton,
      showDocsButton,
      threeDModelUrl
    } = this.props;
    return (
      <StyledInnerWrapper>
        <StyledBreadCrumbs crumbs={breadCrumbs} />
        <SubWrapper>
          <ImageWrapper>
            <OverlayWrapper>
              <Image {...productImage} />
              {archived && (
                <Flag>
                  <FlagText>Out Of Production</FlagText>
                </Flag>
              )}
            </OverlayWrapper>
            {threeDModelUrl && (
              <ThreeDModelLink
                href={threeDModelUrl}
                title={`lochinvar 3d logo that opens a detailed ${productName} product view`}
              >
                <img src="/home/lochinvar3d.png" alt="lochinvar 3d logo" />
              </ThreeDModelLink>
            )}
          </ImageWrapper>

          <DetailsWrapper>
            {productNameHighlight ? (
              <TextHighlight
                Component={ProductTitle}
                data-swiftype-name="title"
                data-swiftype-type="string"
                text={productName}
                highlightText={productNameHighlight}
              />
            ) : (
              <ProductTitle
                data-swiftype-name="title"
                data-swiftype-type="string"
              >
                {productName}
              </ProductTitle>
            )}
            <ProductDescription
              data-swiftype-name="body"
              data-swiftype-type="text"
            >
              {productDescription}
            </ProductDescription>
            {productFeatures && (
              <StyledList dotSize={15.2} items={productFeatures} />
            )}
            <ButtonsWrapper>
              {showDocsButton && (
                <DocsButton onClick={warrantyClicked}>
                  Technical Documents
                </DocsButton>
              )}
              {showCustomTankButton && (
                <StyledNavLink to="/custom-tank">
                  <CustomTankButton>Custom Tank Quote Request</CustomTankButton>
                </StyledNavLink>
              )}
              <RebateWidgetWrapper id="ecorebates" />
            </ButtonsWrapper>
          </DetailsWrapper>
        </SubWrapper>
      </StyledInnerWrapper>
    );
  }
}
